import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, AlertTriangle, Clock, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import { mappingApi } from '../../services/api';
import MappingResults from './MappingResults';
import type { MappingRunStatus } from '../../types';

const MappingRunDetail: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [showApprovalModal, setShowApprovalModal] = useState<null | 'approve' | 'reject'>(null);
  const [approvedBy, setApprovedBy] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mapping', 'run', runId],
    queryFn: async () => (await mappingApi.getRun(runId!)).data,
    enabled: !!runId,
    refetchInterval: (query) => {
      const s = query.state.data?.payload?.status;
      return s === 'pending' || s === 'running' ? 2000 : false;
    },
  });

  const approvalMutation = useMutation({
    mutationFn: (payload: { action: 'approve' | 'reject'; approved_by: string; rejection_reason?: string }) =>
      mappingApi.approveRun(runId!, payload),
    onSuccess: () => {
      setShowApprovalModal(null);
      setApprovedBy('');
      setRejectionReason('');
      refetch();
    },
  });

  const run = data?.payload;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-cargo-accent animate-spin" />
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="max-w-[1400px] mx-auto">
        <div className="bg-cargo-error/10 border border-cargo-error/30 rounded-lg p-6 text-cargo-error">
          Failed to load mapping run
        </div>
      </div>
    );
  }

  const statusConfig: Record<MappingRunStatus, { icon: any; text: string; color: string }> = {
    pending: { icon: Clock, text: 'Pending', color: 'text-cargo-muted' },
    running: { icon: Loader2, text: 'Running', color: 'text-cargo-accent' },
    completed: { icon: CheckCircle2, text: 'Completed', color: 'text-cargo-success' },
    failed: { icon: XCircle, text: 'Failed', color: 'text-cargo-error' },
  };
  const sc = statusConfig[run.status];
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/mapping')}
            className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-cargo-text">Mapping Run</h1>
            <p className="text-cargo-muted text-xs font-mono mt-0.5">{run.run_id}</p>
            {run.description && <p className="text-cargo-muted text-sm mt-1">{run.description}</p>}
          </div>
        </div>

        {/* Approval buttons */}
        {run.status === 'completed' && run.approval_status === 'pending' && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowApprovalModal('reject')}
              className="inline-flex items-center gap-2 px-4 py-2 border border-cargo-error/40 text-cargo-error rounded-lg hover:bg-cargo-error/10 text-sm"
            >
              <ThumbsDown className="w-4 h-4" />
              Reject
            </button>
            <button
              onClick={() => setShowApprovalModal('approve')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-cargo-success text-white rounded-lg hover:bg-cargo-success/90 text-sm"
            >
              <ThumbsUp className="w-4 h-4" />
              Approve
            </button>
          </div>
        )}
      </div>

      {/* Status bar */}
      <div className="bg-cargo-panel border border-cargo-border rounded-lg p-4">
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-cargo-muted text-xs uppercase tracking-wider mb-1">Status</div>
            <div className={`flex items-center gap-2 ${sc.color} font-semibold`}>
              <StatusIcon className={`w-4 h-4 ${run.status === 'running' ? 'animate-spin' : ''}`} />
              {sc.text}
            </div>
          </div>
          <div>
            <div className="text-cargo-muted text-xs uppercase tracking-wider mb-1">Approval</div>
            <div className="text-cargo-text font-semibold capitalize">{run.approval_status}</div>
          </div>
          <div>
            <div className="text-cargo-muted text-xs uppercase tracking-wider mb-1">Duration</div>
            <div className="text-cargo-text font-semibold">
              {run.duration_seconds != null ? `${run.duration_seconds.toFixed(1)}s` : '—'}
            </div>
          </div>
          <div>
            <div className="text-cargo-muted text-xs uppercase tracking-wider mb-1">Coverage</div>
            <div className="text-cargo-text font-semibold">
              {run.coverage_percentage != null ? `${run.coverage_percentage}%` : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Errors */}
      {run.errors && run.errors.length > 0 && (
        <div className="bg-cargo-error/10 border border-cargo-error/30 rounded-lg p-4">
          <div className="flex items-start gap-2 text-cargo-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              {run.errors.map((e: string, i: number) => (
                <div key={i}>{e}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Rejection reason */}
      {run.approval_status === 'rejected' && run.rejection_reason && (
        <div className="bg-cargo-warning/10 border border-cargo-warning/30 rounded-lg p-4 text-sm">
          <div className="text-cargo-muted text-xs uppercase tracking-wider mb-1">Rejected by {run.approved_by}</div>
          <div className="text-cargo-text">{run.rejection_reason}</div>
        </div>
      )}

      {/* Running state */}
      {(run.status === 'pending' || run.status === 'running') && (
        <div className="bg-cargo-panel border border-cargo-border rounded-lg p-12 text-center">
          <Loader2 className="w-10 h-10 text-cargo-accent animate-spin mx-auto mb-4" />
          <p className="text-cargo-text font-medium">Cargo engine is mapping your source tables to the target…</p>
          <p className="text-cargo-muted text-sm mt-2">This takes about 1-3 minutes for typical schemas. Page updates automatically.</p>
        </div>
      )}

      {/* Results */}
      {run.status === 'completed' && run.mapping_result && (
        <MappingResults
          result={run.mapping_result}
          targetDictionary={run.target_dictionary_resolved}
        />
      )}

      {/* Approval modal */}
      {showApprovalModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setShowApprovalModal(null)}
        >
          <div
            className="bg-cargo-panel border border-cargo-border rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-cargo-text font-semibold mb-4">
              {showApprovalModal === 'approve' ? 'Approve Mapping' : 'Reject Mapping'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-cargo-muted text-xs font-medium mb-1.5">Your Name</label>
                <input
                  type="text"
                  value={approvedBy}
                  onChange={(e) => setApprovedBy(e.target.value)}
                  className="w-full px-3 py-2 bg-cargo-surface border border-cargo-border rounded-lg text-cargo-text text-sm"
                  placeholder="Jane Doe"
                />
              </div>
              {showApprovalModal === 'reject' && (
                <div>
                  <label className="block text-cargo-muted text-xs font-medium mb-1.5">Rejection Reason</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-cargo-surface border border-cargo-border rounded-lg text-cargo-text text-sm"
                    placeholder="Explain why this mapping should be rejected…"
                  />
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowApprovalModal(null)}
                disabled={approvalMutation.isPending}
                className="px-4 py-2 text-cargo-muted hover:text-cargo-text text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  approvalMutation.mutate({
                    action: showApprovalModal,
                    approved_by: approvedBy,
                    rejection_reason: showApprovalModal === 'reject' ? rejectionReason : undefined,
                  })
                }
                disabled={
                  approvalMutation.isPending ||
                  !approvedBy ||
                  (showApprovalModal === 'reject' && !rejectionReason)
                }
                className={`px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 ${
                  showApprovalModal === 'approve' ? 'bg-cargo-success hover:bg-cargo-success/90' : 'bg-cargo-error hover:bg-cargo-error/90'
                }`}
              >
                {approvalMutation.isPending ? 'Saving…' : showApprovalModal === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MappingRunDetail;
